/**
 * Plain-language copy for the Search Terms negatives apply states. Pure, so
 * every message is pinned by a unit test rather than found by clicking.
 */
import type {
  ApplySearchTermNegativesResult,
  NegativeApplyStatus,
} from '@/services/googleAdsWeeklyService';

export const SUBMITTING_COPY =
  "Applying to Google Ads, usually under 30 seconds. It keeps going if you close this, so don't apply again.";

// A new apply (the next dialog mints a new key) finishes the rest; resending
// the abandoned key would only replay it.
export const ABANDONED_COPY =
  'This apply stopped partway because the server restarted. Anything already added stays. Start a new apply to finish the rest; nothing is added twice.';

export const NEVER_REACHED_COPY = 'The apply never reached the server. Nothing changed.';

// Retry resends only what was ticked on the list still on screen. After a
// reload (or a client switch) there is nothing left for it to send.
export const RETRY_DROPPED_COPY =
  'Not resent: the list changed after that apply. Tick the rows again on the list now on screen.';

export const LOST_CONTACT_COPY =
  "Lost contact with the server. The apply may still be running, so don't apply again.";

/** What the server is doing, from the last stage it reported finishing. */
const STAGE_PHRASES: Record<string, string> = {
  find_sets: 'setting up the negative lists',
  create_sets: 'setting up the negative lists',
  read_criteria: 'adding the negative keywords',
  write_criteria: 'adding the negative keywords',
  list_campaigns: 'attaching lists to campaigns',
  read_links: 'attaching lists to campaigns',
  write_links: 'attaching lists to campaigns',
};

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

export function checkingCopy(elapsedMs: number, stage?: string): string {
  const doing = stage ? STAGE_PHRASES[stage] : undefined;
  return `Still working in Google Ads (${formatElapsed(elapsedMs)})${doing ? `: ${doing}` : ''}. Don't apply again; this updates when it finishes.`;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function stripPeriod(text: string): string {
  return text.trim().replace(/\.$/, '');
}

type OutcomeFields = Pick<
  ApplySearchTermNegativesResult,
  'applied' | 'attached_campaigns' | 'client_safe_summary'
  | 'already_present' | 'already_attached' | 'failed_keywords' | 'invalid_keywords' | 'applied_keys'
>;

/** The final message for a finished apply. */
export function outcomeCopy(
  status: NegativeApplyStatus | undefined,
  result: OutcomeFields | null | undefined,
): string {
  if (status === 'abandoned' || !result) return ABANDONED_COPY;
  if (status === 'partial') {
    const notAdded = [...(result.failed_keywords ?? []), ...(result.invalid_keywords ?? [])]
      .map((k) => k.keyword);
    return `Partly applied. Added ${result.applied}.${
      notAdded.length ? ` These weren't added: ${notAdded.join(', ')}.` : ''
    }`;
  }
  if (status === 'failed') {
    const detail = stripPeriod(result.client_safe_summary || 'no detail was returned')
      .replace(/^Not applied in Google Ads:\s*/, '');
    const landed = result.applied > 0 ? (result.applied_keys ?? []) : [];
    const already = landed.length
      ? ` Already added: ${landed
        .map((key) => {
          const [kw, match] = key.split('|');
          return `${kw} (${match})`;
        })
        .join(', ')}.`
      : '';
    return `Apply failed in Google Ads: ${detail}.${already}`;
  }
  const present = result.already_present ?? 0;
  const attachedAlready = result.already_attached ?? 0;
  return `Added ${plural(result.applied, 'new negative')}${
    present ? ` (${present} ${present === 1 ? 'was' : 'were'} already there)` : ''
  }. Attached to ${plural(result.attached_campaigns, 'campaign')}${
    attachedAlready ? ` (${attachedAlready} already had the list)` : ''
  }.`;
}

/** Someone's apply is already running for this client. */
export function activeApplyCopy(startedAt: string, actor: string): string {
  const started = new Date(startedAt);
  const hh = String(started.getHours()).padStart(2, '0');
  const mm = String(started.getMinutes()).padStart(2, '0');
  const who = actor.replace(/^(user|agent|system|unknown):/, '');
  return `An apply started at ${hh}:${mm} by ${who} is still running.`;
}

/** A real 4xx (or the ledger missing): nothing was written. */
export function notAppliedCopy(detail: string): string {
  return `Not applied: ${stripPeriod(detail)}. Google Ads was not changed.`;
}

/** The app answered 5xx: the request is over, but some writes may have landed. */
export function appServerErrorCopy(detail: string): string {
  return `Apply failed: ${stripPeriod(detail)}. Some negatives may already be in Google Ads. Check the refreshed list before applying again.`;
}
